import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, FormControl, Stack, Modal, TextField, CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import api from '../network/ApiClient';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { md: 500, xs: '100%' },
  bgcolor: 'background.paper',
  boxShadow: 24,
  maxHeight: '90vh',
  overflowY: 'auto',
  p: { md: 4, xs: 2 },
};

const baseSchema = {
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  phone: Yup.string()
    .matches(/^[0-9]+$/, 'Phone number must only contain digits')
    .min(10, 'Phone number must be at least 10 digits')
    .required('Phone Number is required'),
  email: Yup.string()
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/, 'Please enter a valid email address')
    .required('Email is required'),
};

export default function CreateDriverForm({ onCreateCallback, modalShow, setModalShow, edit, driver }) {
  // Merchant options — dropdown shown only when the caller belongs to more than
  // one merchant; auto-assigned otherwise. Chosen on create only (the backend
  // forbids changing a driver's merchant on update).
  const [optionState, setOptionState] = useState({ data: [] });

  const handleClose = () => {
    setModalShow(false);
  };
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      organizergroup: null,
    },
    validationSchema: Yup.object(
      edit ? baseSchema : { ...baseSchema, organizergroup: Yup.object().nullable().required('Merchant is required') }
    ),

    onSubmit: async (values, { setSubmitting }) => {
      const payload = {
        firstname: values.firstName,
        lastname: values.lastName,
        phone: values.phone,
        email: values.email,
      };
      if (!edit) payload.organizergroup = values.organizergroup?.id;

      try {
        if (edit) {
          await api.put(`driver/${driver.id}`, payload);
        } else {
          await api.post('driver', payload);
        }
        onCreateCallback();
        formik.resetForm();
        handleClose();
      } catch (error) {
        console.error('Error saving driver:', error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const loadOrganizerGroups = async () => {
    try {
      const res = await api.get('organizergroup', { params: { simple: 'ok' } });
      const groups = res.data?.organizer || [];
      setOptionState({ data: groups });
      if (!edit && groups.length === 1 && !formik.values.organizergroup) {
        formik.setFieldValue('organizergroup', groups[0]);
      }
    } catch (e) {
      console.error('Unable to load merchants', e);
    }
  };

  useEffect(() => {
    if (modalShow) {
      formik.resetForm();
      loadOrganizerGroups();
      if (driver) {
        formik.setValues({
          firstName: driver.firstname,
          lastName: driver.lastname,
          phone: driver.phone,
          email: driver.email,
          organizergroup: null,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver, modalShow]);

  const validateFields = () => {
    const errors = {};
    if (!formik.values.firstName) errors.firstName = 'First Name is required';
    if (!formik.values.lastName) errors.lastName = 'Last Name is required';
    if (!formik.values.phone) errors.phone = 'Phone Number is required';
    if (!formik.values.email) errors.email = 'Email is required';
    if (!edit && optionState.data.length > 1 && !formik.values.organizergroup) errors.organizergroup = 'Merchant is required';
    return errors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const errors = validateFields();
    if (Object.keys(errors).length > 0) {
      toast.error(`Please fill in the following fields: ${Object.values(errors).join(', ')}`);
      return;
    }
    formik.handleSubmit();
  };

  return (
    <Modal
      open={modalShow}
      onClose={handleClose}
      aria-labelledby="create-driver-modal"
      aria-describedby="create-driver-form"
    >
      <Box sx={style}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <h4 className="card-title mb-0">{edit ? 'Edit Driver' : 'Create Driver'}</h4>
            <TextField
              fullWidth
              name="firstName"
              label="First Name"
              onChange={formik.handleChange}
              value={formik.values.firstName}
              variant="outlined"
              error={formik.errors.firstName && formik.touched.firstName}
              helperText={formik.errors.firstName && formik.touched.firstName ? formik.errors.firstName : ''}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              name="lastName"
              label="Last Name"
              onChange={formik.handleChange}
              value={formik.values.lastName}
              variant="outlined"
              error={formik.errors.lastName && formik.touched.lastName}
              helperText={formik.errors.lastName && formik.touched.lastName ? formik.errors.lastName : ''}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              name="phone"
              label="Phone Number"
              onChange={formik.handleChange}
              value={formik.values.phone}
              variant="outlined"
              error={formik.errors.phone && formik.touched.phone}
              helperText={formik.errors.phone && formik.touched.phone ? formik.errors.phone : ''}
              InputLabelProps={{ shrink: true }}
              type="number"
            />
            <TextField
              fullWidth
              name="email"
              label="Email"
              type="email"
              onChange={formik.handleChange}
              value={formik.values.email}
              variant="outlined"
              error={formik.errors.email && formik.touched.email}
              helperText={formik.errors.email && formik.touched.email ? formik.errors.email : ''}
              InputLabelProps={{ shrink: true }}
            />

            {/* Merchant can't be changed after creation (the backend forbids it);
                show it read-only so multi-merchant users can see the owner. */}
            {edit && optionState.data.length > 1 ? (
              <TextField
                fullWidth
                label="Merchant"
                value={
                  optionState.data.find((o) => String(o.id) === String(driver?.organizergroup))?.name ||
                  driver?.organizergroup ||
                  '—'
                }
                variant="outlined"
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                disabled
              />
            ) : null}

            {!edit && optionState.data.length > 1 ? (
              <FormControl error={Boolean(formik.errors.organizergroup && formik.touched.organizergroup)} fullWidth>
                <Autocomplete
                  options={optionState.data}
                  onChange={(event, value) => formik.setFieldValue('organizergroup', value)}
                  getOptionLabel={(option) => `${option.name}`}
                  value={formik.values.organizergroup}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Merchant"
                      variant="outlined"
                      error={Boolean(formik.errors.organizergroup && formik.touched.organizergroup)}
                      helperText={formik.errors.organizergroup && formik.touched.organizergroup ? formik.errors.organizergroup : ''}
                    />
                  )}
                />
              </FormControl>
            ) : null}
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button variant="contained" type="submit" disabled={formik.isSubmitting}>
              {formik.isSubmitting ? <CircularProgress size={24} color="inherit" /> : edit ? 'Save' : 'Submit'}
            </Button>
            <Button color="error" onClick={handleClose} disabled={formik.isSubmitting}>
              Cancel
            </Button>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
}
